import cn from 'classnames';
import { FC, MouseEvent } from 'react';
import { useIntl } from 'react-intl';

import { Icon, Tooltip, type IconNames } from '@/shared/ui';

import { i18n } from '../config';

import type {
  TemplateDocumentControls,
  TemplateInsertKind,
} from './TemplateDocumentEditor';
import styles from './TemplateDocumentEditor.module.scss';

const TEMPLATE_VALUE_TOOLS: { kind: TemplateInsertKind; icon: IconNames }[] = [
  { kind: 'teeth', icon: 'toothnum' },
  { kind: 'date', icon: 'calendar' },
  { kind: 'number', icon: 'ruler' },
  { kind: 'table', icon: 'layoutThreeColumns' },
];

type ToolProps = {
  label: string;
  // The icon set has no brackets: an instruction's tool shows «[ ]».
  sign: { icon: IconNames } | { mark: string };
  disabled?: boolean;
  dataComponent: string;
  onClick?: () => void;
};

// A tool keeps the caret in the document, so what it puts in goes where
// the caret is.
const keepCaret = (event: MouseEvent) => event.preventDefault();

const Tool: FC<ToolProps> = ({
  label,
  sign,
  disabled,
  dataComponent,
  onClick,
}) => (
  <Tooltip.Primary content={label} side="bottom">
    <button
      type="button"
      className={cn('p3', styles.tool)}
      aria-label={label}
      disabled={disabled}
      data-component={dataComponent}
      onMouseDown={keepCaret}
      onClick={onClick}
    >
      {'icon' in sign ? (
        <Icon name={sign.icon} size={18} />
      ) : (
        <span className={styles.toolMark} aria-hidden>
          {sign.mark}
        </span>
      )}
    </button>
  </Tooltip.Primary>
);

type Props = {
  // Absent until the editor has loaded.
  controls?: TemplateDocumentControls;
  className?: string;
};

// The template editor's tools in the document's toolbar, after its undo
// and redo, as icons named in their tooltips: the caret's block made a
// heading, a subheading or text, an AI instruction, a choice and a link of
// the selected lines to an option on the left; the other fields on the
// right, before the document's own buttons. Every template shows them all,
// a summary's or an evaluation's fields too: a tool its document cannot
// take is disabled.
export const TemplateEditorTools: FC<Props> = ({ controls, className }) => {
  const { formatMessage } = useIntl();
  if (!controls) {
    return null;
  }

  return (
    <span
      className={cn(styles.tools, className)}
      data-component="workspace-template-tools"
    >
      <span className={styles.toolsDivider} />
      <span className={styles.toolsGroup}>
        <Tool
          sign={{ icon: 'editorTitle' }}
          label={formatMessage({
            id: 'workspace.templateEdit.heading',
            defaultMessage: 'Heading',
          })}
          disabled={!controls.canAddSection}
          dataComponent="workspace-template-tool-heading"
          onClick={controls.heading}
        />
        <Tool
          sign={{ icon: 'editorHeading' }}
          label={formatMessage({
            id: 'workspace.templateEdit.subheading',
            defaultMessage: 'Subheading',
          })}
          disabled={!controls.canAddLines}
          dataComponent="workspace-template-tool-subheading"
          onClick={controls.subheading}
        />
        <Tool
          sign={{ icon: 'editorParagraph' }}
          label={formatMessage({
            id: 'workspace.templateEdit.text',
            defaultMessage: 'Text',
          })}
          disabled={!controls.canAddLines}
          dataComponent="workspace-template-tool-text"
          onClick={controls.text}
        />
        <span className={styles.toolsDivider} />
        <Tool
          sign={{ mark: '[ ]' }}
          label={formatMessage({
            id: 'workspace.templateEdit.instruction',
            defaultMessage: 'Instruction',
          })}
          dataComponent="workspace-template-tool-instruction"
          onClick={controls.instruction}
        />
        <Tool
          sign={{ icon: 'tiles' }}
          label={formatMessage(i18n.noteBlockKindMessages.options)}
          disabled={!controls.canAddLines}
          dataComponent="workspace-template-tool-options"
          onClick={() => controls.insert('options')}
        />
        <Tool
          sign={{ icon: 'link' }}
          label={formatMessage({
            id: 'workspace.templateEdit.link',
            defaultMessage: 'Link',
          })}
          disabled={!controls.canLink}
          dataComponent="workspace-template-tool-link"
          onClick={controls.link}
        />
      </span>
      <span className={cn(styles.toolsGroup, styles.toolsEnd)}>
        {TEMPLATE_VALUE_TOOLS.map(({ kind, icon }) => (
          <Tool
            key={kind}
            sign={{ icon }}
            label={formatMessage(i18n.noteBlockKindMessages[kind])}
            disabled={!controls.canAddLines}
            dataComponent={`workspace-template-tool-${kind}`}
            onClick={() => controls.insert(kind)}
          />
        ))}
      </span>
    </span>
  );
};
